package com.mrpowergamerbr.puppydough

import kotlin.math.min

/**
 * A MD5 (RFC 1321) hasher, because Web Crypto doesn't support MD5
 */
class MD5 {
    companion object {
        private val SHIFTS = intArrayOf(
            7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
            5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
            4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
            6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
        )

        // floor(abs(sin(i + 1)) * 2^32), hardcoded to avoid depending on floating point precision
        private val K = longArrayOf(
            0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
            0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
            0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
            0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
            0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
            0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
            0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
            0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
        ).let { k -> IntArray(64) { k[it].toInt() } }

        fun hash(data: ByteArray) = MD5().apply { update(data) }.digest()

        fun hashHex(data: ByteArray) = hash(data).joinToString("") { (it.toInt() and 0xFF).toString(16).padStart(2, '0') }
    }

    private val state = intArrayOf(0x67452301, 0xefcdab89.toInt(), 0x98badcfe.toInt(), 0x10325476)
    private val buffer = ByteArray(64)
    private var bufferLength = 0
    private var totalLength = 0L
    private val words = IntArray(16)

    fun update(data: ByteArray, offset: Int = 0, length: Int = data.size - offset) {
        totalLength += length
        var i = offset
        val end = offset + length

        // Top up the leftovers of the previous update first
        if (bufferLength > 0) {
            val n = min(64 - bufferLength, length)
            data.copyInto(buffer, bufferLength, i, i + n)
            bufferLength += n
            i += n
            if (bufferLength == 64) {
                processBlock(buffer, 0)
                bufferLength = 0
            }
        }

        // Hash full blocks straight from the input, without copying them
        while (end - i >= 64) {
            processBlock(data, i)
            i += 64
        }

        if (i < end) {
            data.copyInto(buffer, 0, i, end)
            bufferLength = end - i
        }
    }

    fun digest(): ByteArray {
        val bitLength = totalLength * 8

        // 0x80 followed by zeroes, until there's exactly 8 bytes left in the block for the length
        val padding = ByteArray(if (bufferLength < 56) 56 - bufferLength else 120 - bufferLength)
        padding[0] = 0x80.toByte()
        update(padding)
        update(ByteArray(8) { (bitLength ushr (8 * it)).toByte() })

        return ByteArray(16) { (state[it / 4] ushr (8 * (it % 4))).toByte() }
    }

    private fun processBlock(block: ByteArray, offset: Int) {
        for (i in 0 until 16) {
            val o = offset + i * 4
            words[i] = (block[o].toInt() and 0xFF) or
                    ((block[o + 1].toInt() and 0xFF) shl 8) or
                    ((block[o + 2].toInt() and 0xFF) shl 16) or
                    ((block[o + 3].toInt() and 0xFF) shl 24)
        }

        var a = state[0]
        var b = state[1]
        var c = state[2]
        var d = state[3]

        for (i in 0 until 64) {
            var f: Int
            val g: Int
            when (i / 16) {
                0 -> { f = (b and c) or (b.inv() and d); g = i }
                1 -> { f = (d and b) or (d.inv() and c); g = (5 * i + 1) % 16 }
                2 -> { f = b xor c xor d; g = (3 * i + 5) % 16 }
                else -> { f = c xor (b or d.inv()); g = (7 * i) % 16 }
            }
            f += a + K[i] + words[g]
            a = d
            d = c
            c = b
            b += f.rotateLeft(SHIFTS[i])
        }

        state[0] += a
        state[1] += b
        state[2] += c
        state[3] += d
    }
}
